import { API_ENDPOINTS } from "../config";
import { http } from "../utils/http";
import { devLog } from "../utils/devLog";

// Azure AppGW WAF (OWASP CRS 931 RFI) 403s any `://` in a query param,
// which browsers surface as a CORS error. Backend accepts both the short
// code and the full URI; send the short code to stay under the WAF.
const SYSTEM_TO_SHORT_CODE = {
  "http://loinc.org": "loinc",
  "http://snomed.info/sct": "snomed",
  "http://hl7.org/fhir/sid/icd-10": "icd10",
  "http://id.who.int/icd/release/11/mms": "icd11",
};
const toShortSystem = (system) => SYSTEM_TO_SHORT_CODE[system] || system;

class TerminologyService {
  // --- Status ---

  static async getStatus() {
    try {
      return await http.request(API_ENDPOINTS.terminologyStatus, "get");
    } catch (error) {
      devLog("Terminology status check failed:", error);
      return null;
    }
  }

  // --- Search (all return ValueSetExpansionResult) ---

  static async search(type, filter, count = 15) {
    if (!filter || filter.length < 2) {
      return { success: true, total: 0, codes: [] };
    }
    try {
      const url = `${API_ENDPOINTS.terminologySearch(type)}?filter=${encodeURIComponent(filter)}&count=${count}`;
      return await http.request(url, "get");
    } catch (error) {
      devLog(`Terminology search (${type}) failed:`, error);
      return {
        success: false,
        total: 0,
        codes: [],
        errorMessage:
          typeof error.message === "string"
            ? error.message
            : "Search failed",
      };
    }
  }

  static searchConditions(filter, count) {
    return TerminologyService.search("conditions", filter, count);
  }

  static searchDiagnoses(filter, count) {
    return TerminologyService.search("diagnoses", filter, count);
  }

  static searchSymptoms(filter, count) {
    return TerminologyService.search("symptoms", filter, count);
  }

  static searchProcedures(filter, count) {
    return TerminologyService.search("procedures", filter, count);
  }

  static searchMedications(filter, count) {
    return TerminologyService.search("medications", filter, count);
  }

  static async lookupMedication(code) {
    try {
      const url = `${API_ENDPOINTS.terminologyMedicationLookup}?code=${encodeURIComponent(code)}`;
      return await http.request(url, "get");
    } catch (error) {
      devLog("Medication lookup failed:", error);
      return null;
    }
  }

  static searchSocialContext(filter, count) {
    return TerminologyService.search("social-context", filter, count);
  }

  static searchRegimeTherapy(filter, count) {
    return TerminologyService.search("regime-therapy", filter, count);
  }

  static searchAllergies(filter, count) {
    return TerminologyService.search("allergies", filter, count);
  }

  static searchSNOMED(filter, count) {
    return TerminologyService.search("snomed", filter, count);
  }

  static searchICD10(filter, count) {
    return TerminologyService.search("icd10", filter, count);
  }

  static searchICD11(filter, count) {
    return TerminologyService.search("icd11", filter, count);
  }

  static searchLOINC(filter, count) {
    return TerminologyService.search("loinc", filter, count);
  }

  static searchObservablesCombined(filter, count) {
    return TerminologyService.search("observables-combined", filter, count);
  }

  static async searchECL(ecl, filter, count = 15) {
    try {
      const url = `${API_ENDPOINTS.terminologySearch("ecl")}?ecl=${encodeURIComponent(ecl)}&filter=${encodeURIComponent(filter)}&count=${count}`;
      return await http.request(url, "get");
    } catch (error) {
      devLog("Terminology ECL search failed:", error);
      return {
        success: false,
        total: 0,
        codes: [],
        errorMessage: error.message || "ECL search failed",
      };
    }
  }

  // --- Observation Category ---

  static async resolveObservationCategory(system, code) {
    try {
      const url = `${API_ENDPOINTS.terminologyObservationCategory}?system=${encodeURIComponent(toShortSystem(system))}&code=${encodeURIComponent(code)}`;
      const result = await http.request(url, "get");
      return result && result.category ? result.category : null;
    } catch (error) {
      devLog("Observation category resolution failed:", error);
      return null;
    }
  }

  static async resolveObservationCategoryEnhanced(system, code) {
    try {
      const url = `${API_ENDPOINTS.terminologyObservationCategory}/enhanced?system=${encodeURIComponent(toShortSystem(system))}&code=${encodeURIComponent(code)}`;
      const result = await http.request(url, "get");
      return result || null;
    } catch (error) {
      devLog("Enhanced observation category resolution failed:", error);
      return null;
    }
  }

  // --- Observation Set Discovery ---

  static async suggestObservationSet(condition, code, facilityId, patientTypeId) {
    try {
      let url = `${API_ENDPOINTS.terminologyObservationSetSuggest}?condition=${encodeURIComponent(condition)}`;
      if (code) url += `&code=${encodeURIComponent(code)}`;
      if (facilityId) url += `&facilityId=${encodeURIComponent(facilityId)}`;
      if (patientTypeId) url += `&patientTypeId=${encodeURIComponent(patientTypeId)}`;
      return await http.request(url, "get");
    } catch (error) {
      devLog("Observation set suggestion failed:", error);
      return {
        success: false,
        suggestions: [],
        totalFound: 0,
        errorMessage: error.message || "Suggestion failed",
      };
    }
  }

  static async bulkCreateObservationAttributes(facilityId, patientTypeId, attributes) {
    try {
      const url = `${API_ENDPOINTS.terminologyObservationSetBulkCreate}?facilityId=${encodeURIComponent(facilityId)}&patientTypeId=${encodeURIComponent(patientTypeId)}`;
      return await http.request(url, "post", attributes);
    } catch (error) {
      devLog("Bulk observation attribute creation failed:", error);
      return {
        created: [],
        createdCount: 0,
        failed: [],
        failedCount: 0,
        errorMessage: error.message || "Bulk creation failed",
      };
    }
  }

  // --- Pathway Observation Packs (curated, instant — no live CTS lookup) ---

  static async listPathwayPacks(facilityId, patientTypeId) {
    try {
      const params = [];
      if (facilityId) params.push(`facilityId=${encodeURIComponent(facilityId)}`);
      if (patientTypeId) params.push(`patientTypeId=${encodeURIComponent(patientTypeId)}`);
      const url = params.length
        ? `${API_ENDPOINTS.terminologyPathwayPacks}?${params.join("&")}`
        : API_ENDPOINTS.terminologyPathwayPacks;
      const result = await http.request(url, "get");
      return Array.isArray(result) ? result : [];
    } catch (error) {
      devLog("Pathway pack list failed:", error);
      return [];
    }
  }

  static async getPathwayPack(key, facilityId, patientTypeId) {
    try {
      const params = [];
      if (facilityId) params.push(`facilityId=${encodeURIComponent(facilityId)}`);
      if (patientTypeId) params.push(`patientTypeId=${encodeURIComponent(patientTypeId)}`);
      const base = API_ENDPOINTS.terminologyPathwayPackByKey(key);
      const url = params.length ? `${base}?${params.join("&")}` : base;
      return await http.request(url, "get");
    } catch (error) {
      devLog(`Pathway pack fetch (${key}) failed:`, error);
      return { key, observations: [], errorMessage: error.message || "Pack fetch failed" };
    }
  }

  // --- Lookup (returns CodeLookupResult) ---

  static async lookup(type, code) {
    try {
      const url = `${API_ENDPOINTS.terminologyLookup(type)}?code=${encodeURIComponent(code)}`;
      return await http.request(url, "get");
    } catch (error) {
      devLog(`Terminology lookup (${type}) failed:`, error);
      return { found: false, errorMessage: error.message || "Lookup failed" };
    }
  }

  static lookupSNOMED(code) {
    return TerminologyService.lookup("snomed", code);
  }

  static lookupICD10(code) {
    return TerminologyService.lookup("icd10", code);
  }

  static lookupLOINC(code) {
    return TerminologyService.lookup("loinc", code);
  }

  // --- Validation (returns ValidationResult) ---

  static async validate(type, code) {
    try {
      const url = `${API_ENDPOINTS.terminologyValidate(type)}?code=${encodeURIComponent(code)}`;
      return await http.request(url, "get");
    } catch (error) {
      devLog(`Terminology validate (${type}) failed:`, error);
      return {
        result: false,
        message: error.message || "Validation failed",
      };
    }
  }

  static validateSNOMED(code) {
    return TerminologyService.validate("snomed", code);
  }

  // --- Batch Lookup (returns Map of code to CodeLookupResult) ---

  static async batchLookup(type, codes) {
    try {
      const url = API_ENDPOINTS.terminologyBatchLookup(type);
      return await http.request(url, "post", codes);
    } catch (error) {
      devLog(`Terminology batch lookup (${type}) failed:`, error);
      return {};
    }
  }

  static batchLookupSNOMED(codes) {
    return TerminologyService.batchLookup("snomed", codes);
  }

  static batchLookupICD10(codes) {
    return TerminologyService.batchLookup("icd10", codes);
  }

  // --- Translation ---

  static async translateSNOMEDtoICD10(code) {
    try {
      const url = `${API_ENDPOINTS.terminologyTranslate("snomed", "icd10")}?code=${encodeURIComponent(code)}`;
      return await http.request(url, "get");
    } catch (error) {
      devLog("SNOMED to ICD-10 translation failed:", error);
      return {
        success: false,
        errorMessage: error.message || "Translation failed",
      };
    }
  }

  static async translateICD10toSNOMED(code) {
    try {
      const url = `${API_ENDPOINTS.terminologyTranslate("icd10", "snomed")}?code=${encodeURIComponent(code)}`;
      return await http.request(url, "get");
    } catch (error) {
      devLog("ICD-10 to SNOMED translation failed:", error);
      return {
        success: false,
        errorMessage: error.message || "Translation failed",
      };
    }
  }

  // --- Helper: Map search type to search function ---

  static getSearchFnForType(searchType) {
    const searchMap = {
      conditions: TerminologyService.searchConditions,
      diagnoses: TerminologyService.searchDiagnoses,
      symptoms: TerminologyService.searchSymptoms,
      procedures: TerminologyService.searchProcedures,
      medications: TerminologyService.searchMedications,
      "social-context": TerminologyService.searchSocialContext,
      "regime-therapy": TerminologyService.searchRegimeTherapy,
      allergies: TerminologyService.searchAllergies,
      snomed: TerminologyService.searchSNOMED,
      icd10: TerminologyService.searchICD10,
      icd11: TerminologyService.searchICD11,
      loinc: TerminologyService.searchLOINC,
    };
    return searchMap[searchType] || TerminologyService.searchSNOMED;
  }

  static getSearchFnForLookup(terminologyLookup) {
    // Named endpoints — stable, well-tested categories
    const lookupMap = {
      "snomed-conditions": TerminologyService.searchConditions,
      "snomed-diagnoses": TerminologyService.searchDiagnoses,
      "snomed-symptoms": TerminologyService.searchSymptoms,
      "snomed-procedures": TerminologyService.searchProcedures,
      "snomed-medications": TerminologyService.searchMedications,
      "snomed-social-context": TerminologyService.searchSocialContext,
      "snomed-regime-therapy": TerminologyService.searchRegimeTherapy,
      "snomed-allergies": TerminologyService.searchAllergies,
      "snomed": TerminologyService.searchSNOMED,
      "icd10": TerminologyService.searchICD10,
      "icd11": TerminologyService.searchICD11,
      "loinc": TerminologyService.searchLOINC,
    };

    if (lookupMap[terminologyLookup]) {
      return lookupMap[terminologyLookup];
    }

    // Custom ECL subsets — e.g. "ecl:367336001" or "ecl:<< 73430006"
    if (terminologyLookup && terminologyLookup.startsWith("ecl:")) {
      const expr = terminologyLookup.substring(4).trim();
      // If the expression is just a bare concept code (digits only), prepend <<
      // Otherwise the user pasted a full ECL expression — use as-is
      const ecl = /^\d+$/.test(expr) ? `<< ${expr}` : expr;
      return (filter, count) =>
        TerminologyService.searchECL(ecl, filter, count);
    }

    return TerminologyService.searchSNOMED;
  }

  // --- Batch display resolution ---

  static async resolveDisplayNames(codes) {
    if (!codes || codes.length === 0) return {};
    return TerminologyService.batchLookupSNOMED(codes);
  }

  /**
   * Check if a terminologyLookup value is an ECL-based lookup.
   * ECL lookups should eagerly load all results as a dropdown.
   */
  static isECLLookup(terminologyLookup) {
    return terminologyLookup && terminologyLookup.startsWith("ecl:");
  }
}

const terminologyService = TerminologyService;
export default terminologyService;
